// ────────────────────────────────────────────────────────────────────────────
//  useChat.ts — Konversationen & Nachrichten
//
//  Worker (Hono mountet `chat` unter /api/v1/conversations):
//    GET  /conversations                         — Liste
//    POST /conversations  {otherUserId}          — DM lazy-create
//    GET  /conversations/:id/messages            — letzte N msgs
//    GET  /conversations/:id/messages?afterId=N  — incremental polling
//    POST /conversations/:id/messages  {body}    — senden
//    POST /conversations/:id/read                — last_read aktualisieren
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback, useRef } from 'react'
import { api, API_ORIGIN, getToken } from './api'

export interface Message {
  id:              number
  conversationId:  number
  senderId:        string
  senderName:      string
  senderAvatarUrl: string | null
  body:            string
  sentAt:          string
  isSystem:        boolean
  attachmentUrl:   string | null
  attachmentName:  string | null
  attachmentType:  string | null
  attachmentBytes: number | null
}

export interface Conversation {
  id:            number
  title:         string
  type:          'DirectMessage' | 'Group' | 'SupportThread' | string
  orgId:         number | null
  isInfoChannel: boolean
  isReadOnly:    boolean
  memberCount:   number
  unreadCount:   number
  lastMessage:   Message | null
}

// ── Conversations-Liste mit Polling ─────────────────────────────────────────
export function useConversations(opts?: { pollMs?: number }) {
  const [items, setItems] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const r = await api.get<{ items: Conversation[] }>('/conversations')
      setItems(r.items ?? [])
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    void refresh()
    const ms = opts?.pollMs
    if (!ms) return
    const id = setInterval(refresh, ms)
    return () => clearInterval(id)
  }, [refresh, opts?.pollMs])

  /** Chat aus der eigenen Liste entfernen.
   *  Backend: entfernt nur die Membership des aufrufenden Users — wenn
   *  sonst niemand mehr drin ist, wird der ganze Chat gelöscht. Andere
   *  User behalten den Chat weiter (semantisch: „verlassen", nicht „global löschen").
   *  Optimistisch: sofort aus der UI raus, bei Fehler Rollback. */
  const remove = useCallback(async (id: number): Promise<boolean> => {
    const backup = items
    setItems(prev => prev.filter(c => c.id !== id))
    try {
      await api.delete(`/conversations/${id}`)
      return true
    } catch (e: any) {
      // Rollback bei Fehler
      setItems(backup)
      setError(e?.message ?? 'delete failed')
      return false
    }
  }, [items])

  return { items, loading, error, refresh, remove }
}

// ── Messages eines Chats mit incremental-Polling ────────────────────────────
export function useMessages(convId: number | null, opts?: { pollMs?: number }) {
  const [items, setItems] = useState<Message[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastIdRef = useRef<number>(0)

  // Initial-Load — wechselt convId → reset
  const loadInitial = useCallback(async (id: number) => {
    setLoading(true); setError(null); setItems([]); lastIdRef.current = 0
    try {
      const r = await api.get<{ items: Message[] }>(`/conversations/${id}/messages`)
      const arr = r.items ?? []
      setItems(arr)
      lastIdRef.current = arr.length ? arr[arr.length - 1].id : 0
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [])

  // Incremental: nur neue Messages
  const pollNew = useCallback(async (id: number) => {
    try {
      const r = await api.get<{ items: Message[] }>(`/conversations/${id}/messages?afterId=${lastIdRef.current}`)
      const arr = r.items ?? []
      if (arr.length === 0) return
      setItems(prev => [...prev, ...arr])
      lastIdRef.current = arr[arr.length - 1].id
    } catch { /* silent during polling */ }
  }, [])

  useEffect(() => {
    if (convId == null) { setItems([]); lastIdRef.current = 0; return }
    void loadInitial(convId)
    const ms = opts?.pollMs
    if (!ms) return
    const t = setInterval(() => pollNew(convId), ms)
    return () => clearInterval(t)
  }, [convId, loadInitial, pollNew, opts?.pollMs])

  // Senden — optional mit Anhang
  const send = useCallback(async (
    text: string,
    attachment?: ChatAttachment | null,
  ): Promise<Message | null> => {
    if (convId == null) return null
    const trimmed = text.trim()
    if (!trimmed && !attachment) return null
    const payload: Record<string, unknown> = { body: trimmed }
    if (attachment) {
      payload.attachmentUrl   = attachment.attachmentUrl
      payload.attachmentName  = attachment.attachmentName
      payload.attachmentType  = attachment.attachmentType
      payload.attachmentBytes = attachment.attachmentBytes
    }
    const m = await api.post<Message>(`/conversations/${convId}/messages`, payload)
    setItems(prev => [...prev, m])
    if (m.id > lastIdRef.current) lastIdRef.current = m.id
    return m
  }, [convId])

  return { items, loading, error, send }
}

// ── Attachment-Upload (Multipart) ──────────────────────────────────────────
//   Lädt eine Datei zu R2 (über den Worker) und gibt die Anhang-Felder zurück.
//   Diese werden anschließend per send(..., attachment) an POST /messages mitgeschickt.
//   Backend-Whitelist (siehe chat.ts): Bilder + Dokumente, max 25 MB.

export interface ChatAttachment {
  attachmentUrl:   string
  attachmentName:  string
  attachmentType:  'image' | 'file' | string
  attachmentBytes: number
}

export const CHAT_UPLOAD_MAX_BYTES = 25 * 1024 * 1024 // 25 MB

/** Erlaubte Datei-Endungen — muss mit Backend (chat.ts) übereinstimmen. */
export const CHAT_UPLOAD_ALLOWED_EXT: ReadonlyArray<string> = [
  'jpg', 'jpeg', 'png', 'heic', 'heif', 'gif', 'webp',
  'pdf',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'odt', 'ods', 'odp',
  'pages', 'numbers', 'key',
  'txt', 'rtf', 'csv', 'md',
]

/** `accept`-Wert für `<input type=file>` — passt zur Whitelist. */
export const CHAT_UPLOAD_ACCEPT =
  'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.pages,.numbers,.key,.txt,.rtf,.csv,.md'

export function chatUploadExtensionAllowed(filename: string): boolean {
  const ext = filename.toLowerCase().split('.').pop() ?? ''
  return CHAT_UPLOAD_ALLOWED_EXT.includes(ext)
}

export async function uploadChatAttachment(
  convId: number,
  file: File,
): Promise<ChatAttachment> {
  const fd = new FormData()
  fd.append('file', file, file.name)

  const token = getToken()
  const res = await fetch(`${API_ORIGIN}/api/v1/conversations/${convId}/upload`, {
    method:  'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body:    fd,
  })
  const text = await res.text()
  let data: any
  try { data = text ? JSON.parse(text) : {} } catch { data = { error: text } }
  if (!res.ok) {
    throw new Error(data?.error || `Upload fehlgeschlagen (HTTP ${res.status})`)
  }
  return data as ChatAttachment
}

// ── Helpers ─────────────────────────────────────────────────────────────────
export async function openOrCreateDM(otherUserId: string): Promise<Conversation> {
  return api.post<Conversation>('/conversations', { otherUserId })
}

export async function markChatRead(convId: number): Promise<void> {
  await api.post(`/conversations/${convId}/read`, {})
}
